"use client";

import Image from "next/image";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { PageSchema } from "@/components/seo/PageSchema";
import { env } from "@/lib/environment";
import { Typography, Section, Input, Select, CalendlyWidget } from "@/components/ui";
import { schemaOptions } from "./constants";
import CountryList from "country-list-with-dial-code-and-flag";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";
import { useUTMContext } from "@/lib/context/UTMProvider";
import { fireLeadCreated, postServerConversion, setChatGptAdsUser } from "@/lib/tracking/chatgptAds";
import Script from "next/script";
import { trackDemoLead } from "@/lib/tracking/ga";

const clients = [
  {
    logo: env.NEXT_PUBLIC_CDN_URL + "/images/clients/uniliver.xZ9sW-5cU5Pw.svg",
    alt: "Unilever",
    width: "94",
    height: "20",
  },
  {
    logo: env.NEXT_PUBLIC_CDN_URL + "/images/clients/uber.y4tRHwOc9vnD.svg",
    alt: "Uber",
    width: "44",
    height: "16",
  },
  {
    logo: env.NEXT_PUBLIC_CDN_URL + "/images/clients/amazon.7L5vIqoas7CO.svg",
    alt: "Amazon",
    width: "69",
    height: "20",
  },
  {
    logo: env.NEXT_PUBLIC_CDN_URL + "/images/clients/decathlon.N8Juwbkl43DM.svg",
    alt: "Decathlon",
    width: "128",
    height: "20",
  },
];

// Reusable checkmark icon component
const CheckmarkIcon = ({ className = "mt-[4px]" }: { className?: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.8538 6.14625C10.9002 6.19269 10.9371 6.24783 10.9623 6.30853C10.9874 6.36923 11.0004 6.43429 11.0004 6.5C11.0004 6.56571 10.9874 6.63077 10.9623 6.69147C10.9371 6.75217 10.9002 6.80731 10.8538 6.85375L7.35375 10.3538C7.30732 10.4002 7.25217 10.4371 7.19147 10.4623C7.13077 10.4874 7.06571 10.5004 7 10.5004C6.9343 10.5004 6.86923 10.4874 6.80853 10.4623C6.74783 10.4371 6.69269 10.4002 6.64625 10.3538L5.14625 8.85375C5.05243 8.75993 4.99972 8.63268 4.99972 8.5C4.99972 8.36732 5.05243 8.24007 5.14625 8.14625C5.24007 8.05243 5.36732 7.99972 5.5 7.99972C5.63268 7.99972 5.75993 8.05243 5.85375 8.14625L7 9.29313L10.1463 6.14625C10.1927 6.09976 10.2478 6.06288 10.3085 6.03772C10.3692 6.01256 10.4343 5.99961 10.5 5.99961C10.5657 5.99961 10.6308 6.01256 10.6915 6.03772C10.7522 6.06288 10.8073 6.09976 10.8538 6.14625ZM14.5 8C14.5 9.28558 14.1188 10.5423 13.4046 11.6112C12.6903 12.6801 11.6752 13.5132 10.4874 14.0052C9.29973 14.4972 7.99279 14.6259 6.73192 14.3751C5.47104 14.1243 4.31285 13.5052 3.40381 12.5962C2.49477 11.6872 1.8757 10.529 1.6249 9.26809C1.37409 8.00721 1.50282 6.70028 1.99479 5.51256C2.48676 4.32484 3.31988 3.30968 4.3888 2.59545C5.45772 1.88122 6.71442 1.5 8 1.5C9.72335 1.50182 11.3756 2.18722 12.5942 3.40582C13.8128 4.62441 14.4982 6.27665 14.5 8ZM13.5 8C13.5 6.9122 13.1774 5.84883 12.5731 4.94436C11.9687 4.03989 11.1098 3.33494 10.1048 2.91866C9.09977 2.50238 7.9939 2.39346 6.92701 2.60568C5.86011 2.8179 4.8801 3.34172 4.11092 4.11091C3.34173 4.8801 2.8179 5.86011 2.60568 6.927C2.39347 7.9939 2.50238 9.09977 2.91867 10.1048C3.33495 11.1098 4.0399 11.9687 4.94437 12.5731C5.84884 13.1774 6.91221 13.5 8 13.5C9.45819 13.4983 10.8562 12.9184 11.8873 11.8873C12.9184 10.8562 13.4983 9.45818 13.5 8Z"
      fill="#FE4E00"
    />
  </svg>
);
const sanitizePhoneNumber = (value: string) => value.replace(/\D/g, "");

export default function BookDemoPage() {
  const { geoData, isLoading: geoLoading } = useGeoLocation();
  const { getAllTrackingData, getAttributionData } = useUTMContext();
  const hasFiredLeadCreatedRef = useRef(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    country: "US",
    screenCount: "",
  });

  const [phoneValue, setPhoneValue] = useState("");
  const [selectedDialCode, setSelectedDialCode] = useState<string>("+1");
  const [isFormReady, setIsFormReady] = useState(false);

  const countryData = useMemo(() => {
    const countries = CountryList.getAll();
    const countryMap = new Map();

    countries.forEach((country: any) => {
      const code = country.code;
      if (!countryMap.has(code)) {
        countryMap.set(code, {
          code: country.code,
          name: country.name,
          flag: country.flag,
          dialCodes: [] as string[],
        });
      }
      if (country.dialCode) {
        countryMap.get(code).dialCodes.push(country.dialCode);
      }
    });

    return Array.from(countryMap.values())
      .map((country: any) => ({
        ...country,
        phoneCode: country.dialCodes[0] || "+1",
      }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, []);

  const currentCountry = useMemo(() => {
    return countryData.find((c: any) => c.code.toLowerCase() === formData.country.toLowerCase());
  }, [countryData, formData.country]);

  const getAvailableDialCodes = useCallback(
    (countryCode: string) => {
      const found = countryData.find(
        (c: any) => c.code.toLowerCase() === countryCode.toLowerCase()
      );
      return found?.dialCodes || ["+1"];
    },
    [countryData]
  );

  // Auto-detect country and set initial dial code
  useEffect(() => {
    if (geoData?.countryCode) {
      const detected = geoData.countryCode.toLowerCase();
      setFormData(prev => ({ ...prev, country: detected }));
      const codes = getAvailableDialCodes(detected);
      setSelectedDialCode(codes[0]);
    }
    // Mark form as ready after geo detection completes (success or failure)
    if (!geoLoading) {
      setIsFormReady(true);
    }
  }, [geoData, geoLoading, getAvailableDialCodes]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (field === "country") {
      const available = getAvailableDialCodes(value);
      setSelectedDialCode(available[0]);
    }
  };
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  /// handleSubmitDataOnZoho
  const handleSubmitDataOnZoho = async () => {
    try {
      // Get reCAPTCHA token
      const grecaptcha = (window as any).grecaptcha;
      if (!grecaptcha || !grecaptcha.execute) {
        throw new Error("reCAPTCHA not loaded. Please refresh the page and try again.");
      }

      // Wait a bit for reCAPTCHA to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      const token = await grecaptcha.execute(env.NEXT_PUBLIC_GRECAPTCHA_KEY, {
        action: "submit",
      });

      if (!token) {
        throw new Error("Failed to get reCAPTCHA token. Please try again.");
      }

      const sanitizedPhone = sanitizePhoneNumber(phoneValue);

      // Prepare form details
      const details = {
        name: formData.fullName,
        email: formData.email,
        phone: `${selectedDialCode}${sanitizedPhone}`,
        token: token,
        leads_source: "Calendly",
        company_name: "-",
        description: "Number of Screens you plan to deploy - " + formData.screenCount,
        form_name: "book_demo",
        submitted_at: new Date().toISOString(),
        address: {
          country_code: geoData?.countryCode,
          city: geoData?.city,
          region: geoData?.region,
          country: formData.country,
        },
        ...getAllTrackingData(),
      };

      // Submit to API
      const res = await fetch(env.NEXT_PUBLIC_WIDGET_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          details: details,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (result.success) {
        if (!hasFiredLeadCreatedRef.current) {
          hasFiredLeadCreatedRef.current = true;
          trackDemoLead();
          await setChatGptAdsUser(formData.email, `${selectedDialCode}${sanitizedPhone}`);
          const eventId = fireLeadCreated();
          postServerConversion({
            id: eventId,
            type: "lead_created",
            oppref: getAttributionData().oppref,
            sourceUrl: window.location.href,
            email: formData.email,
            phone: `${selectedDialCode}${sanitizedPhone}`,
          });
        }

        // Dispatch Mixpanel tracking event
        // window.dispatchEvent(
        //   new CustomEvent("mixpanelEvent", {
        //     detail: {
        //       type: "book-demo-submitted",
        //       data: {
        //         email: formData.email,
        //       cta_location: "Book a Demo Page",
        //       name: formData.fullName,
        //       phone: `${selectedDialCode}${sanitizedPhone}`,
        //       screen_count: formData.screenCount,
        //     },
        //   },
        // }));
      } else {
        throw new Error(result.message || "Submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const isComplete =
    formData.fullName.trim().length > 0 &&
    formData.email.trim().length > 0 &&
    formData.screenCount.trim().length > 0 &&
    phoneValue.trim().length > 0;

  return (
    <main className="section-bg-gray  pt-[68px] md:pt-[90px]">
      <PageSchema options={schemaOptions} />
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${env.NEXT_PUBLIC_GRECAPTCHA_KEY}`}
        strategy="afterInteractive"
      />

      <Section
        variant="default"
        className="flex py-9 md:pt-[86px] md:pb-[172px] min-h-full flex-col max-md:!bg-none md:bg-contain md:bg-right-bottom md:bg-no-repeat"
        style={{
          backgroundImage: `url('${env.NEXT_PUBLIC_CDN_URL}/images/company/contact-bg.1FQBgqbOVoKT.webp')`,
          backgroundSize: "834px",
        }}
      >
        <div className="flex flex-col h-full lg:flex-row gap-8 md:gap-11 justify-between w-full lg:max-w-[1100px] mx-auto">
          {/* content */}
          <div className="flex flex-col items-start w-full lg:max-w-[538px] pt-0 lg:pt-[37px] pr-3">
            <div className="flex flex-col text-center lg:text-left gap-[13px]">
              <Typography variant="h1" as="h1">
                Book Your <span className="text-accent">30-minute</span> digital signage demo
              </Typography>
              <div className="w-full bg-[#919191]/30 h-[1px] mt-1.5"></div>
            </div>
            <div className="flex-col mt-[30px] gap-4 flex w-full items-center md:items-start">
              <Typography variant="h3" as="h3" className="text-lg text-primary">
                WHAT TO EXPECT:
              </Typography>
              <ul className="w-full p-0 m-0 mt-1 flex flex-col gap-1 pb-1.5">
                <li className="flex flex-row  gap-1.5 items-start">
                  <CheckmarkIcon />
                  <Typography variant="body" as="p" className="text-text-secondary">
                    <strong className="font-bold">A personalized walkthrough</strong> of Pickcel
                  </Typography>
                </li>
                <li className="flex flex-row gap-2 font-normal text-base text-text-secondary">
                  <CheckmarkIcon />
                  <Typography variant="body" as="p" className="text-text-secondary">
                    <strong className="font-bold">Best practices</strong> tailored to your industry
                  </Typography>
                </li>
                <li className="flex flex-row gap-2 font-normal text-base text-text-secondary">
                  <CheckmarkIcon />
                  <Typography variant="body" as="p" className="text-text-secondary">
                    <strong className="font-bold">Insights</strong> to help you make the{" "}
                    <strong className="font-bold">business case</strong>
                  </Typography>
                </li>
              </ul>
            </div>
            <div className=" flex-col gap-3 mt-8 hidden lg:flex">
              <Image
                src={`${env.NEXT_PUBLIC_CDN_URL}/images/shared/captera-g2.0K7YO82_kXp9.svg`}
                alt="capterra review"
                title="capterra"
                width={320}
                height={38}
                className="w-[338px]"
              />
              <div className="w-full bg-[#919191]/30 h-[1px] mt-1.5"></div>
              <Typography variant="small" as="p">
                Powering 150,000+ screens for 9,000+ businesses in 70+ countries.
              </Typography>
              <div className="flex flex-wrap items-center gap-10 mt-6">
                {clients.map((client, index) => (
                  <div key={index}>
                    <Image
                      src={client.logo}
                      alt={client.alt}
                      width={parseInt(client.width)}
                      height={parseInt(client.height)}
                      loading="lazy"
                      decoding="async"
                      className="md:aspect-auto h-5 md:h-auto"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col w-full lg:max-w-[476px] bg-white shadow-default rounded-sm">
            {!isFormReady ? (
              <div className="w-full flex items-center justify-center py-8 px-6 md:py-7 md:px-9 rounded-lg min-h-[400px]">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <Typography variant="small" as="p" className="text-text-secondary">
                    Loading form...
                  </Typography>
                </div>
              </div>
            ) : (
              <form
                className="w-full flex flex-col gap-4 py-8 px-6 md:py-7 md:px-9 rounded-lg"
                onSubmit={handleFormSubmit}
              >
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  label="Full Name*"
                  placeholder="Full Name"
                  size="md"
                  value={formData.fullName}
                  onChange={e => handleInputChange("fullName", e.target.value)}
                  required
                />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  label="Business Email Address*"
                  placeholder="Enter your business email"
                  size="md"
                  value={formData.email}
                  onChange={e => handleInputChange("email", e.target.value)}
                  required
                />

                <Select
                  label="How many screens do you plan to start with?*"
                  id="screenCount"
                  aria-label="screens"
                  rightIcon={
                    <svg width="16" height="16" viewBox="0 0 20 20">
                      <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  }
                  size="md"
                  value={formData.screenCount}
                  onChange={e => handleInputChange("screenCount", e.target.value)}
                  required
                >
                  <option value="" disabled>
                    Please Select
                  </option>
                  <option value="1-5">1 - 5</option>
                  <option value="6-20">6 - 20</option>
                  <option value="21-200">21 - 200</option>
                  <option value="201+">201+</option>
                </Select>

                {/* Phone with Country Selector */}
                <div className="flex flex-col gap-1.5">
                  <label
                    id="phone-label"
                    htmlFor="phoneNumber"
                    className="text-sm font-medium text-text-primary"
                  >
                    Phone Number*
                  </label>
                  <div className="flex flex-row w-full gap-2">
                    {/* Country flag trigger with native select overlay showing names */}
                    <div className="relative">
                      <div className="w-10 h-11 rounded-lg border border-[#D7D9DE] bg-white flex items-center justify-center text-base">
                        <span aria-hidden>{currentCountry?.flag || "🇺🇸"}</span>
                      </div>
                      <select
                        id="country"
                        name="country"
                        aria-label="Country"
                        value={formData.country}
                        onChange={e => handleInputChange("country", e.target.value)}
                        className="absolute inset-0 w-14 h-12 opacity-0 cursor-pointer"
                        required
                      >
                        {countryData.map((country: any) => (
                          <option key={country.code} value={country.code.toLowerCase()}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Phone input with dial code */}
                    <div className="h-11 w-full rounded-lg border border-[#D7D9DE] bg-white px-4 flex items-center gap-2 text-[18px] text-text-secondary">
                      {currentCountry && currentCountry.dialCodes.length > 1 ? (
                        <select
                          value={selectedDialCode}
                          onChange={e => setSelectedDialCode(e.target.value)}
                          className="text-text-primary bg-transparent outline-none border-none cursor-pointer text-sm md:text-base font-medium"
                        >
                          {currentCountry.dialCodes.map((dialCode: string) => (
                            <option key={dialCode} value={dialCode}>
                              {dialCode}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-text-primary text-sm md:text-base font-medium">
                          {selectedDialCode}
                        </span>
                      )}
                      <input
                        id="phoneNumber"
                        name="phoneNumber"
                        type="tel"
                        placeholder="Enter phone number"
                        value={phoneValue}
                        onChange={e => setPhoneValue(e.target.value.replace(/\D/g, ""))}
                        className="flex-1 h-full bg-transparent outline-none placeholder:text-text-secondary/80 text-sm md:text-base font-medium"
                        inputMode="tel"
                        pattern="[0-9]*"
                        autoComplete="tel"
                        aria-labelledby="phone-label"
                        required
                      />
                    </div>
                  </div>
                </div>
                <CalendlyWidget
                  buttonText="Book free demo"
                  buttonVariant="primary"
                  buttonSize="lg"
                  buttonTrackingId="hero-book-a-demo"
                  disabled={!isComplete}
                  autoPopulate={{
                    name: formData.fullName,
                    email: formData.email,
                    phone: phoneValue ? `${selectedDialCode}${phoneValue}` : null,
                    screen_count: formData.screenCount,
                  }}
                  onButtonClick={() => {
                    handleSubmitDataOnZoho();
                  }}
                />
                <Typography
                  variant="small"
                  as="p"
                  className="text-text-secondary text-xs mx-auto italic"
                >
                  No credit card. No spam. GDPR-compliant.
                </Typography>
              </form>
            )}
          </div>

          <div className="flex-col items-center gap-3 flex lg:hidden">
            <Image
              src={`${env.NEXT_PUBLIC_CDN_URL}/images/shared/captera-g2.0K7YO82_kXp9.svg`}
              alt="capterra review"
              title="capterra"
              width={320}
              height={38}
              className="w-[338px]"
            />
            <div className="w-full bg-[#919191]/30 h-[1px] mt-1.5"></div>
            <Typography variant="small" as="p" className="text-center">
              Powering 150,000+ screens for 9,000+ businesses in 70+ countries.
            </Typography>
            <div className="flex flex-wrap justify-center items-center gap-10 mt-3">
              {clients.map((client, index) => (
                <div key={index}>
                  <Image
                    src={client.logo}
                    alt={client.alt}
                    width={parseInt(client.width)}
                    height={parseInt(client.height)}
                    loading="lazy"
                    decoding="async"
                    className="md:aspect-auto h-5 md:h-auto"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </main>
  );
}
